import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import DatePicker from "react-datepicker"

import { httpOk, GET, POST } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

/**
 * Смены сразу нескольким сотрудникам: отметил бригаду или должность целиком, поставил
 * «пришли в 9:00, ушли в 18:00» — и одна отправка вместо окна на каждого.
 *
 * Уход раньше прихода считаем уходом на следующий день (ночная смена), а не ошибкой.
 * Если у сотрудника в этот день уже есть смена, по умолчанию его пропускаем, чтобы
 * повторное сохранение не задвоило часы; «Заменить» — массовая правка времени.
 */

const emptyForm = {
	employee_ids: [],
	date: '',
	check_in: '09:00',
	check_out: '18:00',
	note: '',
	mode: 'skip',
}

function toMinutes(time) {
	if (!time) return null
	const [hours, minutes] = time.split(':').map(Number)
	return hours * 60 + minutes
}

function addDays(date, days) {
	const [year, month, day] = date.split('-').map(Number)
	return formatDate(new Date(year, month - 1, day + days), 'yyyy-MM-dd')
}

function CreateBulk({ show, onHide, onSaved, posId, initial = {}, attendanceUsers = [] }) {
	const { t } = useTranslation()

	const [employees, setEmployees] = useState([])
	const [form, setForm] = useState(emptyForm)
	const [search, setSearch] = useState('')

	async function getEmployees() {
		const response = await GET('/helper/employees', posId ? { pos_id: posId } : {})
		if (httpOk(response)) setEmployees(response.data || [])
	}

	useEffect(() => {
		if (!show) return

		setForm({
			...emptyForm,
			date: formatDate(new Date(), 'yyyy-MM-dd'),
			...initial,
		})
		setSearch('')
		getEmployees()
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	// Сотрудники по должностям — должность отмечается целиком одной галочкой
	const groups = useMemo(() => {
		const searchLower = search.trim().toLowerCase()
		const result = []

		employees
			.filter(employee => !searchLower || String(employee.name || '').toLowerCase().includes(searchLower))
			.forEach(employee => {
				const key = employee.employee_position_id || 0
				var group = result.find(item => item.key === key)
				if (!group) {
					group = { key: key, name: employee.position_name || t('without_position'), employees: [] }
					result.push(group)
				}
				group.employees.push(employee)
			})

		// «Без должности» в конце списка
		return result.sort((a, b) => (a.key === 0) - (b.key === 0) || a.name.localeCompare(b.name))
	}, [employees, search, t])

	// Смены выбранного дня из уже загруженной таблицы — чтобы видеть, кого «Пропустить» не тронет
	const shiftsOnDate = useMemo(() => {
		const result = {}
		attendanceUsers.forEach(user => {
			const shifts = user.dates?.[form.date]?.shifts || []
			if (shifts.length > 0) result[user.employee_id] = shifts
		})
		return result
	}, [attendanceUsers, form.date])

	const visibleIds = groups.flatMap(group => group.employees.map(employee => employee.id))
	const selectedWithShift = form.employee_ids.filter(id => shiftsOnDate[id]).length

	const checkInMinutes = toMinutes(form.check_in)
	const checkOutMinutes = toMinutes(form.check_out)
	const isNextDay = checkInMinutes !== null && checkOutMinutes !== null && checkOutMinutes <= checkInMinutes
	const durationMinutes = (checkInMinutes !== null && checkOutMinutes !== null)
		? (checkOutMinutes - checkInMinutes + (isNextDay ? 1440 : 0))
		: null

	function toggleIds(ids, checked) {
		const rest = form.employee_ids.filter(id => !ids.includes(id))
		setForm({ ...form, employee_ids: checked ? [...rest, ...ids] : rest })
	}

	async function save(e) {
		e.preventDefault()

		if (form.employee_ids.length === 0) {
			toast.error(t('attendance_select_employees'))
			return
		}

		const payload = {
			employee_ids: form.employee_ids,
			check_in_time: `${form.date} ${form.check_in}:00`,
			check_out_time: form.check_out
				? `${isNextDay ? addDays(form.date, 1) : form.date} ${form.check_out}:00`
				: null,
			note: form.note,
			mode: form.mode,
		}

		const response = await POST('/users-attendances-bulk', payload, { loader: true })
		if (!httpOk(response)) return

		const result = response.data || {}
		var message = t('attendance_bulk_created', { value: result.created || 0 })
		if (result.replaced > 0) {
			message += ', ' + t('attendance_bulk_replaced', { value: result.replaced })
		}
		toast.success(message)

		if ((result.skipped || []).length > 0) {
			toast.warning(t('attendance_bulk_skipped', { value: result.skipped.join(', ') }), { autoClose: 8000 })
		}

		onSaved()
	}

	return (
		<Modal show={show} animation={false} centered size="lg" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t('attendance_bulk_title')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form onSubmit={save} autoComplete="off">
					<div className="row">
						<div className="col-md-6">
							<div className="d-flex justify-content-between align-items-center mb-2">
								<label className="mb-0">{t('employees')}<span className="required-mark">*</span></label>
								<span className="text-muted">{t('selected_count', { value: form.employee_ids.length })}</span>
							</div>

							<input type="text" className="form-control mb-2" placeholder={t('search')}
								value={search}
								onChange={(e) => setSearch(e.target.value)} />

							<div className="border rounded p-2" style={{ height: '360px', overflowY: 'auto' }}>
								{visibleIds.length > 0 &&
									<div className="form-check mb-2">
										<input className="form-check-input" type="checkbox" id="bulk-all"
											checked={visibleIds.every(id => form.employee_ids.includes(id))}
											onChange={(e) => toggleIds(visibleIds, e.target.checked)} />
										<label className="form-check-label fw-bold" htmlFor="bulk-all">{t('select_all')}</label>
									</div>
								}

								{groups.map(group => {
									const ids = group.employees.map(employee => employee.id)
									const checkedCount = ids.filter(id => form.employee_ids.includes(id)).length

									return (
										<div className="mb-2" key={group.key}>
											<div className="form-check bg-light rounded py-1">
												<input className="form-check-input" type="checkbox" id={'bulk-group-' + group.key}
													checked={checkedCount === ids.length}
													ref={(el) => { if (el) el.indeterminate = checkedCount > 0 && checkedCount < ids.length }}
													onChange={(e) => toggleIds(ids, e.target.checked)} />
												<label className="form-check-label fw-bold w-100" htmlFor={'bulk-group-' + group.key}>
													{group.name}
													<span className="text-muted fw-normal ms-1">{checkedCount}/{ids.length}</span>
												</label>
											</div>

											{group.employees.map(employee => (
												<div className="form-check ms-3" key={employee.id}>
													<input className="form-check-input" type="checkbox" id={'bulk-employee-' + employee.id}
														checked={form.employee_ids.includes(employee.id)}
														onChange={(e) => toggleIds([employee.id], e.target.checked)} />
													<label className="form-check-label d-flex justify-content-between w-100"
														htmlFor={'bulk-employee-' + employee.id}>
														<span>{employee.name}</span>
														{shiftsOnDate[employee.id] &&
															<span className="text-muted small" title={t('attendance_has_shift')}>
																<i className="uil uil-clock-three me-1"></i>
																{shiftsOnDate[employee.id].map(shift =>
																	formatDate(shift.check_in_time, 'HH:mm') + '–' + (shift.check_out_time ? formatDate(shift.check_out_time, 'HH:mm') : '…')
																).join(', ')}
															</span>
														}
													</label>
												</div>
											))}
										</div>
									)
								})}

								{groups.length === 0 &&
									<div className="text-muted text-center py-3">{t('list_empty')}</div>
								}
							</div>
						</div>

						<div className="col-md-6">
							<div className="form-group">
								<label>{t('date')}<span className="required-mark">*</span></label>
								<div className="calendar-input">
									<DatePicker
										className="form-control"
										dateFormat="dd.MM.yyyy"
										minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
										maxDate={new Date(new Date().getFullYear(), 11, 31)}
										selected={form.date ? new Date(form.date + 'T00:00:00') : ''}
										onChange={(date) => setForm({ ...form, date: date ? formatDate(date, 'yyyy-MM-dd') : '' })} />
									<i className="uil uil-calendar-alt"></i>
								</div>
							</div>

							<div className="d-flex gap-2">
								<div className="form-group w-100">
									<label>{t('check_in_time')}<span className="required-mark">*</span></label>
									<input type="time" className="form-control" required
										value={form.check_in}
										onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
								</div>
								<div className="form-group w-100">
									<label>{t('check_out_time')}</label>
									<input type="time" className="form-control"
										value={form.check_out}
										onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
								</div>
							</div>

							{durationMinutes !== null &&
								<div className="text-muted mb-3">
									{t('time')}: <b>{Math.floor(durationMinutes / 60)}:{String(durationMinutes % 60).padStart(2, '0')}</b>
									{isNextDay &&
										<span className="text-warning ms-2">
											<i className="uil uil-moon me-1"></i>{t('attendance_next_day')}
										</span>
									}
								</div>
							}

							<div className="form-group">
								<label>{t('attendance_existing_title')}</label>
								<div className="form-check">
									<input className="form-check-input" type="radio" id="bulk-mode-skip"
										checked={form.mode === 'skip'}
										onChange={() => setForm({ ...form, mode: 'skip' })} />
									<label className="form-check-label" htmlFor="bulk-mode-skip">{t('attendance_existing_skip')}</label>
								</div>
								<div className="form-check">
									<input className="form-check-input" type="radio" id="bulk-mode-replace"
										checked={form.mode === 'replace'}
										onChange={() => setForm({ ...form, mode: 'replace' })} />
									<label className="form-check-label" htmlFor="bulk-mode-replace">{t('attendance_existing_replace')}</label>
								</div>
								{selectedWithShift > 0 &&
									<div className="text-warning small mt-1">
										{t('attendance_employees_with_shift', { value: selectedWithShift })}
									</div>
								}
							</div>

							<div className="form-group">
								<label>{t('note')}</label>
								<textarea className="form-control" rows="2" maxLength="255"
									value={form.note}
									onChange={(e) => setForm({ ...form, note: e.target.value })} />
							</div>
						</div>
					</div>

					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100" onClick={onHide}>
							{t('cancel')}
						</button>
						<button type="submit" className="btn btn-primary w-100"
							disabled={!form.date || !form.check_in || form.employee_ids.length === 0}>
							{t('save')} {form.employee_ids.length > 0 && `(${form.employee_ids.length})`}
						</button>
					</div>
				</form>
			</Modal.Body>
		</Modal>
	)
}

export default CreateBulk
